import {
  IStyles,
  InputField,
  InstructionsPanel,
  Loader,
  MultiSelect,
  NoContentCard,
  PrimaryButton,
  TimePicker,
  ToggleSwitch,
  deserify,
  getLocalStorage,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Timestamp } from '@bufbuild/protobuf';
import { Box, Typography } from '@mui/material';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AssignmentConfigEnum,
  SubmissionType,
} from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import { ASSESSMENT_CONGRATULATIONS } from '../../../routeHandling/RoutesNomenclature';
import {
  combineStartTimeAndDate,
  convertTimeStringToTimestamp,
} from '../../../utils/functions';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import {
  setCreatedAssessmentDetails,
  setTemporarySectionIds,
} from '../../Assessment/reducer/assessmentFlow.slice';
import {
  useCurrentMinute,
  wrongEndTime,
  wrongStartTime,
} from '../../Assessment/utils/assessment.utils';
import { StartAndEndPointPicker } from '../../Assessment/Common/AssignAssessment/components/StartAndEndPointPicker';
import { getLearningHwErrorMessage } from '../utils';

// Duplicated from Assessment/Common/AssignAssessment, custom homework branch only (demo only).
const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(30) },
    padding: {
      xs: `${pxToRem(20)} ${pxToRem(20)} ${pxToRem(80)}`,
      lg: `${pxTovW(40)} ${pxTovW(241)}`,
    },
    boxSizing: 'border-box',
  },
  formBox: {
    display: { md: 'flex' },
    gap: { xs: pxToRem(20), md: pxTovW(36) },
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(30) },
    width: { xs: '100%', md: pxTovW(856) },
  },
  inputBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
  },
  inputField: {
    height: { xs: pxToRem(50), md: pxTovW(70) },
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    border: '1px solid #CCE6FE',
    width: '100%',
    justifyContent: 'center',
  },
};

interface ISection {
  id: bigint;
  name: string;
  selected: boolean;
}

export const LearningHwAssign = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const assessmentIdParam = searchParams.get('assessmentId');
  const assessmentId =
    assessmentIdParam && /^\d+$/.test(assessmentIdParam)
      ? BigInt(assessmentIdParam)
      : undefined;
  const minute = useCurrentMinute();
  const teacher_id = getLocalStorage('userId');
  const { setSelectedFunction } = useGlobalContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { class_subject_info } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const {
    LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();

  const [assessment, setAssessment] = useState<AssessmentTask>();
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );
  const [submitting, setSubmitting] = useState(false);
  const [taskName, setTaskName] = useState('Learning Homework');
  const [dailyReminder, setDailyReminder] = useState('18:00');
  const [reattemptAllowed, setReattemptAllowed] = useState(true);
  const [sectionList, setSectionList] = useState<ISection[]>(
    user_info?.teachClassSubjects
      ?.filter((elem) => class_subject_info?.classId === elem.classId)
      .map((elem) => ({
        id: elem.sectionId,
        name: elem.sectionName,
        selected: class_subject_info?.sectionId === elem.sectionId,
      })) || []
  );
  const [startTimeValues, setStartTimeValues] = useState({
    timeValue: `${new Date().getHours()} : ${new Date().getMinutes()}`,
    dateValue: new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString(),
  });
  const [endTimeValues, setEndTimeValues] = useState({
    timeValue: '23:59',
    dateValue: new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString(),
  });

  useEffect(() => {
    setSelectedFunction(() => () => navigate(-1));
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  useEffect(() => {
    if (!assessmentId) {
      setLoading('error');
      return;
    }
    (async () => {
      try {
        const response =
          await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
            {
              personId: teacher_id,
              personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              assessmentId,
            }
          );
        setAssessment(response?.data?.assessment);
        setLoading(response?.data?.assessment ? 'completed' : 'error');
      } catch (err) {
        console.error(err);
        setLoading('error');
      }
    })();
  }, [assessmentId]);

  const timesInvalid =
    wrongStartTime({ startTimeValues }).wrongDate ||
    wrongStartTime({ startTimeValues }).wrongTime ||
    wrongEndTime({ startTimeValues, endTimeValues }).wrongDate ||
    wrongEndTime({ startTimeValues, endTimeValues }).wrongTime;
  const selectedSectionIds = sectionList
    .filter((val) => val.selected)
    .map((val) => val.id);

  const toastError = (label: string) =>
    dispatch(setToastInfo({ label, variant: 'error', open: true }));

  const assignClickHandler = async () => {
    if (!assessment) return;
    const now = new Date().toISOString();
    // A start time in the past becomes "now", as in the existing assign page.
    const startIso =
      now > combineStartTimeAndDate(startTimeValues)
        ? now
        : combineStartTimeAndDate(startTimeValues);
    const startTime = new Date(startIso);
    const endTime = new Date(combineStartTimeAndDate(endTimeValues));
    if (endTime < new Date())
      return toastError('Homework deadline has passed.');
    if (startTime >= endTime)
      return toastError('Start date and time must be before the end.');
    if (taskName.trim() === '')
      return toastError('Homework name cannot be empty');

    try {
      setSubmitting(true);
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.assessmentAssignmentSubmit(
          {
            assessmentId: assessment.assessmentId,
            assessmentName: taskName.trim(),
            assessmentInstructions: assessment.assessmentInstructions,
            assignmentInfo: [
              {
                configName: AssignmentConfigEnum.ASSIGNMENT_CONFIG_REATTEMPT,
                isEnabled: reattemptAllowed,
              },
            ],
            dailyReminderTime: convertTimeStringToTimestamp(dailyReminder),
            startTime: Timestamp.fromDate(startTime),
            endTime: Timestamp.fromDate(endTime),
            sectionIds: selectedSectionIds,
            teacherId: teacher_id,
          }
        );
      if (response?.data) {
        // The existing congratulations page reads these from the store.
        dispatch(setCreatedAssessmentDetails(response.data));
        dispatch(setTemporarySectionIds(selectedSectionIds));
        navigate(
          `${ASSESSMENT_CONGRATULATIONS}/${response.data.updatedAssessment?.assessmentId}`
        );
      }
    } catch (err) {
      console.error(err);
      toastError(getLearningHwErrorMessage(err, 'Assign homework failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading === 'loading') return <Loader />;
  if (loading === 'error' || !assessment)
    return (
      <NoContentCard
        variant="error"
        icon="error"
        text="Could not load the homework. Please go back and try again."
      />
    );

  return (
    <Box sx={styles.root}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h1">Assign Learning Homework</Typography>
          <Typography variant="cardText" sx={{ color: 'primary.main' }}>
            Class {class_subject_info?.classname || ''}
            {class_subject_info?.section || ''} |{' '}
            {class_subject_info?.subject || ''}
          </Typography>
        </Box>
        <PrimaryButton
          key={`assign_${minute}`}
          disabled={
            submitting || timesInvalid || selectedSectionIds.length === 0
          }
          onClick={assignClickHandler}
          sx={{
            position: { xs: 'fixed', md: 'unset' },
            bottom: { xs: pxToRem(10) },
            left: { xs: '5%' },
            width: { xs: '90%', md: 'auto' },
            zIndex: { xs: 100, md: 0 },
          }}
        >
          ASSIGN
        </PrimaryButton>
      </Box>

      <Box sx={styles.formBox}>
        <Box sx={styles.fields}>
          <Box sx={styles.inputBox}>
            <Typography variant="h3">Homework Name</Typography>
            <InputField
              nonCircular
              value={taskName}
              name="taskName"
              onChange={(e) => setTaskName(e.target.value)}
              variant="outlined"
              sx={styles.inputField}
            />
          </Box>
          <StartAndEndPointPicker
            startTimeValues={startTimeValues}
            setStartTimeValues={setStartTimeValues}
            endTimeValues={endTimeValues}
            setEndTimeValues={setEndTimeValues}
          />
          <Box sx={styles.inputBox}>
            <Typography variant="h3">Daily Reminder</Typography>
            <TimePicker
              fullWidth
              value={dailyReminder}
              name="dailyReminder"
              handleTimeChange={(time) => setDailyReminder(time)}
            />
          </Box>
          <Box sx={styles.inputBox}>
            <Typography variant="h3">Choose Section</Typography>
            <MultiSelect
              selectAllTitle="All Divisions"
              inputName="homeworkSection"
              handleChange={() => undefined}
              dataList={sectionList}
              setDataList={setSectionList}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: pxToRem(10) }}>
            <Typography variant="bodyText">Reattempt</Typography>
            <ToggleSwitch
              checked={reattemptAllowed}
              handleChange={() => setReattemptAllowed((prev) => !prev)}
            />
          </Box>
          <Typography variant="bodyText">
            {assessment.taskMetaInfo?.resourceIds.length ?? 0} resources
            assigned ·{' '}
            {assessment.submissionType === SubmissionType.SUBMISSION_OFFLINE
              ? 'Resources only'
              : 'Students upload on App'}
          </Typography>
        </Box>
        <Box sx={{ mt: { xs: pxToRem(20), md: 0 } }}>
          <InstructionsPanel
            customSx={{
              display: 'flex',
              flexGrow: 1,
              width: { xs: '100%', md: pxTovW(900), lg: pxTovW(555) },
              padding: { xs: 0, md: pxToRem(20) },
            }}
            editIconDisplay
            Katexvalue={assessment.assessmentInstructions?.[0]}
            AttachmentList={assessment.taskUploads.filter(
              (e) => e.isDeleted === false
            )}
            onRedirect={() => navigate(-1)}
          />
        </Box>
      </Box>
    </Box>
  );
};
